import _ from 'underscore';
import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Callout,
  DetailsList,
  DetailsListLayoutMode,
  DirectionalHint,
  IconButton,
  SelectionMode,
} from '@fluentui/react';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
// import { useOnActivity } from '@bingads-webui-campaign-react/mca-core';

import { LocationTargetErrorType } from './location-targeting-error-type';
import { getStyles } from '../styles/location-targeting-error-style';

const errorStringsMap = {
  [LocationTargetErrorType.REDUNDANT_LOCATION]: {
    title: _TL_('This location is a duplicate target'),
    description: _TL_('This location is either part of another targeted location or contains a smaller targeted location.'),
    fixDescription: _TL_('You can remove the duplicate locations or adjust them to avoid duplicate targets.'),
  },
  [LocationTargetErrorType.FACEBOOK_AND_GOOGLE_NOT_SUPPORTED]: {
    title: _TL_('Facebook and Google don\'t support this location'),
    description: _TL_('Note: Your ads will be eligible to run on Bing, even if you make no changes to this location.'),
    fixDescription: _TL_('To show your ads on Facebook and Google, try increasing the size of your target area.'),
  },
  [LocationTargetErrorType.FACEBOOK_NOT_SUPPORTED]: {
    title: _TL_('Facebook doesn\'t support targeting this location'),
    description: _TL_('Note: Your ads will be eligible to run on Bing and Google, even if you make no changes to this location.'),
    fixDescription: _TL_('To show your ads on Facebook, try increasing the size of your target area.'),
  },
  [LocationTargetErrorType.GOOGLE_NOT_SUPPORTED]: {
    title: _TL_('Google doesn\'t support targeting this location'),
    description: _TL_('Note: Your ads will be eligible to run on Bing and Facebook, even if you make no changes to this location.'),
    fixDescription: _TL_('To show your ads on Google, try increasing the size of your target area.'),
  },
};

/**
 * Callout that is displayed when viewing more detailed information on an erroneous location target.
 * @param {Object} props - React props
 * @returns {ReactNode} - React component.
 */
function LocationTargetErrorCalloutUnstyled(props) {
  const {
    classes,
    error,
    locationTargets,
    onDismiss,
    onLocationTargetRemove,
    target,
  } = props;

  const i18n = useContext(I18nContext);
  // const onSignalActivity = useOnActivity();
  const errorStrings = useMemo(() => errorStringsMap[error], [error]);

  const isSingleLocationTarget = useMemo(() => _.size(locationTargets) === 1, [locationTargets]);

  const handleLocationTargetRemove = useCallback((item) => {
    // onSignalActivity(`remove location target: ${JSON.stringify(item)}`);
    onLocationTargetRemove(item);
    onDismiss();
  }, [onDismiss, onLocationTargetRemove]);

  const columns = useMemo(() => [
    {
      key: 'label',
      name: i18n.getString(_TL_('Label')),
      className: 'column-label',
      onRender: (_item, index) => {
        if (isSingleLocationTarget) {
          return i18n.getString(_TL_('Location'));
        }
        return i18n.getString(_TL_('Location {{number}}'), { number: index + 1 });
      },
      isCollapsible: true,
      isResizable: false,
      minWidth: 80,
      maxWidth: 80,
    },
    {
      key: 'nameAndRadius',
      name: i18n.getString(_TL_('Location')),
      className: 'column-name-and-radius',
      fieldName: 'ExpandedName',
      isResizable: false,
      onRender: item => <span title={item.ExpandedName}>{item.ExpandedName}</span>,
      minWidth: 240,
      maxWidth: 240,
    },
    {
      key: 'removeButton',
      name: i18n.getString(_TL_('Remove')),
      className: 'column-remove',
      minWidth: 30,
      maxWidth: 30,
      isResizable: false,
      onRender: item => (
        <IconButton
          ariaLabel={i18n.getString(_TL_('Remove'))}
          className="remove-button"
          iconProps={{ iconName: 'Delete' }}
          onClick={() => handleLocationTargetRemove(item)}
          title={i18n.getString(_TL_('Remove'))}
        />
      ),
    },
  ], [i18n, isSingleLocationTarget, handleLocationTargetRemove]);

  if (_.isEmpty(errorStrings)) {
    return null;
  }

  return (
    <Callout
      className={classNames('location-target-error-callout', classes.locationTargetErrorCallout)}
      directionalHint={DirectionalHint.rightCenter}
      hidden={!target}
      onDismiss={onDismiss}
      target={target}
    >
      <h3>{i18n.getString(errorStrings.title)}</h3>
      <p>{i18n.getString(errorStrings.description)}</p>
      <p><strong>{i18n.getString(_TL_('How you can fix this:'))}</strong></p>
      <p>{i18n.getString(errorStrings.fixDescription)}</p>

      {locationTargets &&
        <DetailsList
          className="location-target-error-callout-details-list"
          columns={columns}
          isHeaderVisible={false}
          items={locationTargets}
          layoutMode={DetailsListLayoutMode.justified}
          selectionMode={SelectionMode.none}
        />
      }
    </Callout>
  );
}

LocationTargetErrorCalloutUnstyled.propTypes = {
  classes: PropTypes.shape({ locationTargetErrorCallout: PropTypes.string }).isRequired,
  error: PropTypes.oneOf(_.values(LocationTargetErrorType)),
  locationTargets: PropTypes.arrayOf(PropTypes.shape({})),
  onDismiss: PropTypes.func.isRequired,
  onLocationTargetRemove: PropTypes.func.isRequired,
  target: PropTypes.shape({}),
};

LocationTargetErrorCalloutUnstyled.defaultProps = {
  error: undefined,
  locationTargets: undefined,
  target: undefined,
};

export const LocationTargetErrorCallout =
  withDefaultStyles(LocationTargetErrorCalloutUnstyled, getStyles);
